// src/store/userSlice.ts
// Importe les fonctions `createSlice` et `PayloadAction` depuis Redux Toolkit.
// `createSlice` simplifie la création d'un "slice" (tranche) de l'état Redux, et `PayloadAction` typifie les actions qui passent des données.
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Définit l'interface `UserState`, qui décrit la forme de l'état utilisateur dans Redux.
// Ici, `UserState` a une seule propriété `role`, qui peut être soit une chaîne (string) soit `null`.
interface UserState {
  role: "TRAVELER" | "RESIDENT" | null;
  pseudo: string | null;
  notifNumber: number | null;
  isConnect: boolean;
  badge: string[];
  region: string | null;
}

// Définit l'état initial `initialState` pour ce slice, basé sur l'interface `UserState`.
// Au début, `role` est défini sur `null`, indiquant qu'aucun rôle d'utilisateur n'est sélectionné ou connecté.
const initialState: UserState = {
  role: null,
  pseudo: null,
  notifNumber: null,
  isConnect: false,
  badge: [],
  region: null
};

// Utilise `createSlice` pour créer un slice Redux pour gérer l'état utilisateur.
// Un "slice" est une partie de l'état Redux qui comprend son propre état initial, ses actions et son réducteur.
const userSlice = createSlice({
  name: 'user', // Nom de ce slice, utilisé pour identifier les actions dans Redux.
  initialState, // L'état initial pour ce slice (défini précédemment comme `{ role: null }`).
  reducers: {  // Les "reducers" sont des fonctions qui définissent comment modifier l'état pour ce slice.
    // `updateUserRole` : Un réducteur qui met à jour le rôle de l'utilisateur.
    // `PayloadAction<string>` précise que cette action attend une chaîne de caractères comme charge utile (le rôle).
    // Lorsque `updateUserRole` est appelé, l'état `role` prend la valeur passée par l'action.
    updateUserRole: (state, action: PayloadAction<"TRAVELER" | "RESIDENT" | null>) => {
      state.role = action.payload;
    },

    updateNotifNumber: (state, action: PayloadAction<number | null>) => {
      state.notifNumber = action.payload;
    },

    updateIsConnect: (state, action: PayloadAction<boolean>) => {
      state.isConnect = action.payload;
    },

    updatePseudo: (state, action: PayloadAction<string | null>) => {
      state.pseudo = action.payload;
    },

    updateBadge: (state, action: PayloadAction<string[]>) => {
      state.badge = action.payload;
    },

    updateRegion: (state, action: PayloadAction<string | null>) => {
      state.region = action.payload;
    },

    // `resetUser` : Un réducteur qui réinitialise les datas du slice du user en les définissant à `null`.
    // Cette action est utile pour effacer le rôle, le pseudo... lors de la déconnexion ou du nettoyage de l'état utilisateur.
    resetUser: (state) => {
      state.role = null;
      state.notifNumber = null;
      state.isConnect = false;
      state.pseudo = null;
      state.badge = [];
      state.region = null;
    },
  },
});

// Exporte les actions `updateUserRole` , 'updateNotifNumber' et `resetUserRole` pour qu'elles soient disponibles dans l'application.
// Ces actions seront utilisées pour déclencher les changements d'état correspondants via `dispatch`.
export const { updateUserRole, resetUser, updateNotifNumber, updateIsConnect, updatePseudo, updateBadge, updateRegion } = userSlice.actions;

// Exporte le réducteur de ce slice pour qu'il puisse être ajouté au store Redux.
// Ce réducteur (généré automatiquement par `createSlice`) gère les actions pour ce slice.
export default userSlice.reducer;